import React, { useState, useEffect } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { supabase } from './lib/supabase';
import Login from './components/Auth/Login';
import SignUp from './components/Auth/SignUp';
import GameCard from './components/Games/GameCard';
import Leaderboard from './components/Standings/Leaderboard';
import AnalysisView from './components/Analysis/AnalysisView';
import DataLoader from './components/Admin/DataLoader';
import PickResultsController from './components/Admin/PickResultsController';
import { API_BASE_URL } from './lib/config';
import { CURRENT_SEASON, AVAILABLE_WEEKS, getCurrentNFLWeek } from './config/season';
import { useColorMode } from './theme/ColorModeProvider';
import { useFeedback } from './contexts/FeedbackContext';
import './App.css';

interface Game {
  id: number;
  week: number;
  season: number;
  home_team: string;
  away_team: string;
  game_date: string;
  home_spread: number;
  total_points?: number;
  home_score?: number;
  away_score?: number;
  is_final: boolean;
  game_id?: string;
  competition_id?: string;
  locked?: boolean;
}

interface Pick {
  id: number;
  game_id: number;
  pick_value: string;
}

// Client-side live-refresh cadence. Mirrors the server scheduled function:
// every 10 minutes normally, every 5 minutes within an hour of a kickoff.
const REFRESH_NORMAL_MS = 10 * 60 * 1000;
const REFRESH_FAST_MS = 5 * 60 * 1000;
const FAST_WINDOW_MS = 60 * 60 * 1000;

const computeRefreshMs = (games: Game[]): number => {
  const now = Date.now();
  const upcoming = games
    .map((g) => new Date(g.game_date).getTime() - now)
    .filter((ms) => ms > 0);
  if (upcoming.length === 0) return REFRESH_NORMAL_MS;
  return Math.min(...upcoming) <= FAST_WINDOW_MS ? REFRESH_FAST_MS : REFRESH_NORMAL_MS;
};

const MainApp: React.FC = () => {
  const { user, profile, isAdmin, signOut, loading } = useAuth();
  const { mode, toggle } = useColorMode();
  const { notify, confirm } = useFeedback();
  const [currentView, setCurrentView] = useState<'games' | 'standings' | 'analysis' | 'admin'>('games');
  const [games, setGames] = useState<Game[]>([]);
  const [userPicks, setUserPicks] = useState<{ [gameId: number]: Pick }>({});
  const [pendingPicks, setPendingPicks] = useState<{ [gameId: number]: string }>({});
  const [currentWeek, setCurrentWeek] = useState(getCurrentNFLWeek());
  const [currentSeason] = useState(CURRENT_SEASON);
  const [loadingGames, setLoadingGames] = useState(false);
  const [availableWeeks] = useState(AVAILABLE_WEEKS);
  const [submittingPicks, setSubmittingPicks] = useState(false);
  const [refreshingOdds, setRefreshingOdds] = useState(false);
  const [lockCheckInterval, setLockCheckInterval] = useState<NodeJS.Timeout | null>(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (user) {
      fetchCurrentWeekGames();
      fetchUserPicks();
    }
  }, [user, currentWeek]);

  // Real-time lock checking (every 60s)
  useEffect(() => {
    if (games.length > 0) {
      if (lockCheckInterval) {
        clearInterval(lockCheckInterval);
      }

      const interval = setInterval(() => {
        checkGameLockStatus();
      }, 60000); // 60 seconds

      setLockCheckInterval(interval);

      return () => {
        if (interval) {
          clearInterval(interval);
        }
      };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [games]);

  // Live-view auto-refresh: while viewing the current NFL week, periodically re-read
  // games from the DB so the server-updated lines appear without a manual click.
  // The server keeps the data fresh; this is a read-only re-fetch (no ESPN, no writes).
  useEffect(() => {
    if (!user || currentWeek !== getCurrentNFLWeek()) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;

    const schedule = (delay: number) => {
      timer = setTimeout(async () => {
        if (cancelled) return;
        const fresh = await fetchGamesForWeek(currentWeek, true);
        if (!cancelled) schedule(computeRefreshMs(fresh));
      }, delay);
    };

    schedule(computeRefreshMs(games));

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, currentWeek]);

  const checkGameLockStatus = async () => {
    try {
      const gameIds = games.map((game) => game.id).join(',');
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      const headers: Record<string, string> = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(
        `${API_BASE_URL}/api/picks/lock-status?gameIds=${gameIds}`,
        {
          headers,
          credentials: 'include',
        }
      );

      if (response.ok) {
        const lockStatuses = await response.json();

        const updatedGames = games.map((game) => {
          const lockStatus = lockStatuses.find((ls: any) => ls.id === game.id);
          if (lockStatus) {
            return {
              ...game,
              locked:
                lockStatus.lock_status === 'locked' ||
                lockStatus.lock_status === 'should_be_locked',
            };
          }
          return game;
        });

        setGames(updatedGames);
      }
    } catch (error) {
      console.error('Error checking lock status:', error);
    }
  };

  // Fetch games for a week. `silent` skips the loading spinner so background
  // auto-refreshes update spreads in place without replacing the grid.
  const fetchGamesForWeek = async (week: number, silent = false): Promise<Game[]> => {
    try {
      if (!silent) setLoadingGames(true);
      const response = await fetch(`${API_BASE_URL}/api/games?week=${week}&season=${currentSeason}`);
      if (response.ok) {
        const data = await response.json();
        // Filter to only show Sunday games (check in Eastern Time)
        const sundayGames = (data || []).filter((game: Game) => {
          const gameDate = new Date(game.game_date);
          const easternDate = new Date(gameDate.toLocaleString('en-US', { timeZone: 'America/New_York' }));
          return easternDate.getDay() === 0; // 0 = Sunday
        });
        setGames(sundayGames);
        return sundayGames;
      }
      return [];
    } catch (error) {
      console.error('Failed to fetch games:', error);
      return [];
    } finally {
      if (!silent) setLoadingGames(false);
    }
  };

  const fetchCurrentWeekGames = () => fetchGamesForWeek(currentWeek);

  const fetchUserPicks = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) return;

      const response = await fetch(
        `${API_BASE_URL}/api/picks/my-picks?week=${currentWeek}&season=${currentSeason}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const picks = await response.json();
        const picksByGame = picks.reduce((acc: any, pick: Pick) => {
          acc[pick.game_id] = pick;
          return acc;
        }, {});
        setUserPicks(picksByGame);
      }
    } catch (error) {
      console.error('Failed to fetch user picks:', error);
    }
  };

  const handleMakePick = async (gameId: number, pickValue: string) => {
    const game = games.find((g) => g.id === gameId);
    if (!game) {
      notify('Game not found.', 'error');
      return;
    }

    if (game.locked) {
      notify('This game is locked and no longer accepting picks.', 'warning');
      return;
    }

    if (game.is_final) {
      notify('This game has already finished.', 'warning');
      return;
    }

    const msUntilGame = new Date(game.game_date).getTime() - Date.now();
    if (msUntilGame <= 0) {
      notify('Cannot make picks after a game has started.', 'warning');
      return;
    }

    // Warn if the game is starting very soon
    const minutesUntilGame = Math.floor(msUntilGame / (1000 * 60));
    if (minutesUntilGame <= 5) {
      const confirmed = await confirm({
        title: 'Game starting soon',
        message: `⚠️ This game starts in ${minutesUntilGame} minute${minutesUntilGame !== 1 ? 's' : ''} and will lock soon.\n\nPick ${pickValue === 'home' ? game.home_team : game.away_team}?`,
        confirmText: 'Yes, pick it',
        cancelText: 'Cancel',
        severity: 'warning',
      });
      if (!confirmed) return;
    }

    setPendingPicks((prev) => ({
      ...prev,
      [gameId]: pickValue,
    }));
  };

  const submitAllPicks = async () => {
    try {
      setSubmittingPicks(true);
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) return;

      // Filter out picks for games that have already started (Eastern Time)
      const validPicks = Object.entries(pendingPicks).filter(([gameId]) => {
        const game = games.find((g) => g.id === parseInt(gameId));
        if (!game) return false;
        const gameTime = new Date(game.game_date);
        const nowEastern = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }));
        return gameTime > nowEastern;
      });

      if (validPicks.length === 0) {
        notify('No valid picks to submit — those games may have started.', 'warning');
        return;
      }

      const pickPromises = validPicks.map(async ([gameId, pickValue]) => {
        const response = await fetch(`${API_BASE_URL}/api/picks`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            gameId: parseInt(gameId),
            pickValue,
          }),
        });

        const result = { gameId, response, data: null as any, error: null as any };

        if (response.ok) {
          result.data = await response.json();
        } else {
          try {
            result.error = await response.json();
          } catch {
            result.error = { error: `HTTP ${response.status}` };
          }
        }

        return result;
      });

      const results = await Promise.all(pickPromises);
      const successful = results.filter((r) => r.response.ok);
      const failed = results.filter((r) => !r.response.ok);

      if (successful.length > 0) {
        const successfulGameIds = successful.map((s) => s.gameId);
        setPendingPicks((prev) => {
          const updated = { ...prev };
          successfulGameIds.forEach((gameId) => {
            delete updated[parseInt(gameId)];
          });
          return updated;
        });

        await fetchUserPicks();
        notify(
          `${successful.length} pick${successful.length !== 1 ? 's' : ''} submitted successfully!`,
          'success'
        );
      }

      if (failed.length > 0) {
        const lockErrors = failed.filter((f) => f.response.status === 423);
        const otherErrors = failed.filter((f) => f.response.status !== 423);

        if (lockErrors.length > 0) {
          const lockedGames = lockErrors.map((e) => {
            const game = games.find((g) => g.id === parseInt(e.gameId));
            return game ? `${game.home_team} vs ${game.away_team}` : 'Unknown game';
          });

          notify(
            `${lockErrors.length} game(s) locked before your pick landed: ${lockedGames.join(', ')}.`,
            'warning'
          );

          fetchGamesForWeek(currentWeek);
        }

        if (otherErrors.length > 0) {
          notify(
            `${otherErrors.length} pick(s) failed to submit due to a server error. Please try again.`,
            'error'
          );
        }
      }
    } catch (error) {
      console.error('Failed to submit picks:', error);
      notify('Something went wrong submitting your picks. Please try again.', 'error');
    } finally {
      setSubmittingPicks(false);
    }
  };

  const handleWeekChange = (week: number) => {
    setCurrentWeek(week);
    setPendingPicks({}); // Clear pending picks when changing weeks
    fetchGamesForWeek(week);
  };

  // Manual refresh: re-read the latest lines from the DB (kept fresh by the server
  // scheduled function). Read-only — no ESPN fetch and no DB writes from the browser.
  const refreshOdds = async () => {
    try {
      setRefreshingOdds(true);
      const fresh = await fetchGamesForWeek(currentWeek, true);
      notify(`Lines refreshed — ${fresh.length} game${fresh.length !== 1 ? 's' : ''} loaded.`, 'success');
    } catch (err) {
      console.error('Error refreshing lines:', err);
      notify('Could not refresh lines. Please try again.', 'error');
    } finally {
      setRefreshingOdds(false);
    }
  };

  // Filter games to only those that haven't started (check in Eastern Time)
  const activeGames = games.filter((game) => {
    const gameTime = new Date(game.game_date);
    const nowEastern = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }));
    return gameTime > nowEastern;
  });

  const allPicksMade = activeGames.every((game) => userPicks[game.id] || pendingPicks[game.id]);

  const validPendingPicks = Object.entries(pendingPicks).filter(([gameId]) => {
    const game = games.find((g) => g.id === parseInt(gameId));
    if (!game) return false;
    const gameTime = new Date(game.game_date);
    const nowEastern = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }));
    return gameTime > nowEastern;
  });
  const hasPendingPicks = validPendingPicks.length > 0;

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="app">
      <header className="app-header">
        <h1>🏈 7i Legends Pick Em</h1>
        {user && (
          <nav className="nav">
            <button
              className={currentView === 'games' ? 'active' : ''}
              onClick={() => setCurrentView('games')}
            >
              Games
            </button>
            <button
              className={currentView === 'standings' ? 'active' : ''}
              onClick={() => setCurrentView('standings')}
            >
              Standings
            </button>
            <button
              className={currentView === 'analysis' ? 'active' : ''}
              onClick={() => setCurrentView('analysis')}
            >
              Analysis
            </button>
            {isAdmin && (
              <button
                className={currentView === 'admin' ? 'active' : ''}
                onClick={() => setCurrentView('admin')}
              >
                Admin
              </button>
            )}
            <div className="user-info">
              <button
                className="theme-toggle"
                onClick={toggle}
                aria-label="Toggle color theme"
                title={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {mode === 'dark' ? '☀️' : '🌙'}
              </button>
              {profile?.display_name && (
                <span className="user-greeting">Hello, {profile.display_name}!</span>
              )}
              <button onClick={signOut} className="signout-btn">
                Sign Out
              </button>
            </div>
          </nav>
        )}
      </header>

      <main className="main-content">
        {currentView === 'games' && (
          <div className="games-section">
            <div className="week-header">
              <h2>Week {currentWeek} - {currentSeason} Season (Sunday Games Only)</h2>
              <div style={{ display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
                <div className="week-selector">
                  <label htmlFor="week-select" style={{ marginRight: '10px' }}>
                    Select Week:
                  </label>
                  <select
                    id="week-select"
                    className="week-select"
                    value={currentWeek}
                    onChange={(e) => handleWeekChange(Number(e.target.value))}
                    disabled={loadingGames}
                  >
                    {availableWeeks.map((week) => (
                      <option key={week} value={week}>
                        Week {week}
                      </option>
                    ))}
                  </select>
                </div>
                {currentWeek === getCurrentNFLWeek() && (
                  <button
                    className="week-button"
                    onClick={refreshOdds}
                    disabled={refreshingOdds || games.length === 0}
                    style={{ opacity: refreshingOdds ? 0.6 : 1 }}
                  >
                    {refreshingOdds ? 'Refreshing…' : 'Refresh Lines'}
                  </button>
                )}
              </div>
            </div>

            {loadingGames ? (
              <div className="loading">Loading games...</div>
            ) : games.length > 0 ? (
              <div className="games-grid">
                {games.map((game) => (
                  <GameCard
                    key={game.id}
                    game={game}
                    userPick={userPicks[game.id]}
                    pendingPick={pendingPicks[game.id]}
                    onMakePick={handleMakePick}
                  />
                ))}
              </div>
            ) : (
              <div className="no-games">
                No Sunday games available for week {currentWeek}.
              </div>
            )}
          </div>
        )}

        {currentView === 'standings' && (
          <Leaderboard season={currentSeason} week={currentWeek} />
        )}

        {currentView === 'analysis' && (
          <AnalysisView season={currentSeason} />
        )}

        {currentView === 'admin' && isAdmin && (
          <div className="admin-section">
            <DataLoader />
            <PickResultsController />
          </div>
        )}

        {currentView === 'admin' && !isAdmin && (
          <div className="unauthorized">
            <h2>Access Denied</h2>
            <p>You don't have permission to access the admin section.</p>
          </div>
        )}
      </main>

      {/* Floating Submit Button */}
      {currentView === 'games' && hasPendingPicks && (
        <div className="floating-submit">
          <div className="submit-info">
            <span className="pending-count">
              {validPendingPicks.length} valid pick{validPendingPicks.length !== 1 ? 's' : ''} pending
            </span>
            {allPicksMade && (
              <span className="ready-indicator">✓ All active picks made!</span>
            )}
          </div>
          <button
            className="submit-all-button"
            onClick={submitAllPicks}
            disabled={submittingPicks || validPendingPicks.length === 0}
          >
            {submittingPicks ? 'Submitting...' : `Submit ${validPendingPicks.length} Pick${validPendingPicks.length !== 1 ? 's' : ''}`}
          </button>
        </div>
      )}
    </div>
  );
};

const AuthenticatedApp: React.FC = () => {
  const [authMode, setAuthMode] = useState<'login' | 'signup'>('login');
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!user) {
    return (
      <div className="auth-app">
        {authMode === 'login' ? (
          <Login onToggleMode={() => setAuthMode('signup')} />
        ) : (
          <SignUp onToggleMode={() => setAuthMode('login')} />
        )}
      </div>
    );
  }

  return <MainApp />;
};

function App() {
  return (
    <AuthProvider>
      <AuthenticatedApp />
    </AuthProvider>
  );
}

export default App;
